/* Buying more of something already on the screen you are looking at.
 *
 * The add-a-position screen exists for a company the book has never held. For
 * one it already holds, it is the wrong instrument: it asks for a thesis and a
 * ceiling that are already written down, and it writes a second lot into an
 * account that already has one — which `Holding` is documented not to allow and
 * which splits the average cost across two rows that each know half of it.
 *
 * This averages into the lot instead, the way the contract screen has always
 * done, and asks only for what actually differs between one purchase and the
 * next: how many, at what, and what the broker took. */

import { useState } from 'react';
import { accountName } from '../lib/accounts';
import { moneyExact, price as fmtPrice, shares as fmtShares, shortDate, todayIso , isDay} from '../lib/format';
import { newId } from '../lib/id';
import { decimalOr, isPositiveDecimal } from '../lib/number';
import { addToLot, lotFor } from '../state/addToLot';
import { effectOf, moveCash, settle } from '../state/cashMove';
import { buyCost } from '../state/commission';
import { doubtAboutPrice } from '../state/priceSanity';
import { useStore } from '../state/store';
import type { Account, Position, Trade } from '../types';

export function AddToPosition({ p, say }: { p: Position; say: (m: string) => void }) {
  const { state, book, putHolding, putTrade, setCash } = useStore();
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState<Account>(p.holdings[0]?.account ?? state.data.accounts[0]?.id ?? '');
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [fee, setFee] = useState('');
  /* When it happened, not when it is being typed in.
   *
   * The form stamped today on both the trade and, for a first purchase into an
   * account, the lot's opening date. A purchase recorded a week late was then
   * filed a week late — and the date is not decoration here: every marker on
   * the price chart is placed by it, `fifoRealised` matches the earliest parcel
   * first by it, and `sharesOn` reads it to say how many shares a dividend was
   * paid on. Today stays the default, because most purchases are recorded the
   * day they happen. */
  const [at, setAt] = useState(todayIso());
  const [saving, setSaving] = useState(false);

  const usable = state.data.accounts.filter((a) => a.isFutures !== true);
  const bought = decimalOr(shares);
  const paid = decimalOr(price);
  const feePaid = decimalOr(fee);
  const ready = isPositiveDecimal(shares) && isPositiveDecimal(price) && account !== ''
    && isDay(at);
  const priceDoubt = doubtAboutPrice(paid, p.price, bought);

  const lot = lotFor(state.data.holdings, p.ticker, account);
  const preview = lot && ready ? addToLot(lot, { shares: bought, price: paid, fee: feePaid }) : null;

  const reset = () => { setShares(''); setPrice(''); setFee(''); setOpen(false); };

  const commit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const existing = lotFor(state.data.holdings, p.ticker, account);
      const outcome = existing ? addToLot(existing, { shares: bought, price: paid, fee: feePaid }) : null;

      if (existing && outcome) {
        await putHolding(outcome.next);
      } else {
        /* No lot in this account yet. A first purchase there is a new lot, not
           an average of nothing — and it carries the ticker's own fetch symbol
           rather than one derived from the name (§3.2). */
        await putHolding({
          id: newId('hold'),
          ticker: p.ticker,
          yahooSymbol: p.yahooSymbol,
          account,
          shares: bought,
          avgCost: paid + (bought > 0 ? feePaid / bought : 0),
          currency: p.currency,
          openedAt: at,
        });
      }

      const trade: Trade = {
        id: newId('trade'),
        ticker: p.ticker,
        account,
        side: 'buy',
        shares: bought,
        price: paid,
        /* Noon, so the day is unambiguous wherever it is read back. A date
           input gives a day and nothing else, and stamping midnight puts a
           purchase on the day before for anyone west of UTC. */
        at: `${at}T12:00:00.000Z`,
      };
      if (feePaid > 0) trade.fee = feePaid;

      const spend = effectOf(moveCash(
        book.cashByAccount, state.data.accounts, account,
        -buyCost(bought, paid, feePaid), p.currency, state.fx,
      ));
      if (spend) trade.cashEffect = spend;

      await putTrade(trade);
      for (const m of settle(book.cashByAccount, [spend])) {
        await setCash(m.account, m.amount, m.currency);
      }

      say(outcome
        ? `Added ${fmtShares(bought)}. The average on ${accountName(state.data.accounts, account)} is now ${fmtPrice(outcome.next.avgCost, p.currency)}.`
        : `Recorded ${fmtShares(bought)} of ${p.ticker} in ${accountName(state.data.accounts, account)}.`);
      reset();
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-block" style={{ marginTop: 12 }} onClick={() => setOpen(true)}>
        Add to this position
      </button>
    );
  }

  return (
    <div className="card card-pad" style={{ marginTop: 12 }}>
      <div className="section-head" style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: 13 }}>Buying more {p.ticker}</h2>
      </div>

      <div className="field">
        <label htmlFor="add-account">Into</label>
        <select id="add-account" className="select" value={account}
                onChange={(e) => setAccount(e.target.value)}>
          {usable.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}{lotFor(state.data.holdings, p.ticker, a.id) ? '' : ' · nothing here yet'}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="add-shares">Shares</label>
          <input id="add-shares" className="input" data-mono="true" inputMode="decimal"
                 value={shares} onChange={(e) => setShares(e.target.value)} placeholder="0" />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="add-price">Price paid</label>
          <input id="add-price" className="input" data-mono="true" inputMode="decimal"
                 value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
        </div>
      </div>

      {priceDoubt ? (
        <div className="note" data-tone="warn" style={{ marginBottom: 12 }}>
          {paid.toFixed(2)} is {priceDoubt.multiple.toFixed(1)}× the current price of{' '}
          {p.price.toFixed(2)}.
          {priceDoubt.totalLooksRight ? (
            <>
              {' '}If that was the total for {bought} shares, the price per share is{' '}
              <strong>{priceDoubt.ifTotal.toFixed(2)}</strong>. This field is per share.
            </>
          ) : (
            <> This field is per share.</>
          )}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="add-fee">Commission ({p.currency})</label>
          <input id="add-fee" className="input" data-mono="true" inputMode="decimal"
                 value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0.00" />
        </div>
        {/* The day it happened, which is not always the day it is written down.
            Everything dated reads this: the markers on the price chart, the
            earliest-parcel-first matching on the tax screen, and the share count
            a dividend was paid on. */}
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="add-at">When you bought</label>
          <input id="add-at" className="input" type="date" max={todayIso()}
                 value={at} onChange={(e) => setAt(e.target.value)} />
        </div>
      </div>
      {at !== todayIso() ? (
        <div className="hint" style={{ marginTop: -6, marginBottom: 12 }}>
          Recorded on {shortDate(at)}, not today.
          {!lotFor(state.data.holdings, p.ticker, account)
            ? ' This is the first purchase in that account, so it also becomes the date the'
              + ' position opened there.'
            : ''}
        </div>
      ) : null}

      {ready ? (
        <div className="kv-list" style={{ marginBottom: 12 }}>
          <div className="kv">
            <span className="dim">Leaves the account</span>
            <span className="num" style={{ marginLeft: 'auto' }}>
              {moneyExact(buyCost(bought, paid, feePaid), p.currency as 'USD')}
            </span>
          </div>
          {preview ? (
            <div className="kv">
              <span className="dim">Average there</span>
              <span className="num" style={{ marginLeft: 'auto' }}>
                {preview.costStillUnknown
                  ? 'still unknown'
                  : <>{fmtPrice(preview.previousAvg, p.currency)} → {fmtPrice(preview.next.avgCost, p.currency)}</>}
              </span>
            </div>
          ) : (
            <div className="kv">
              <span className="dim">This account</span>
              <span className="num" style={{ marginLeft: 'auto' }}>opens a new lot</span>
            </div>
          )}
        </div>
      ) : null}

      {preview?.costStillUnknown ? (
        <div className="hint hint-warn" style={{ marginBottom: 10 }}>
          That lot has no cost on record, and a known price averaged into an unknown one cannot
          produce a known average, so it stays unknown. Correct the lot below first if you want the
          figure to mean something.
        </div>
      ) : null}

      <div className="hint" style={{ marginBottom: 10 }}>
        This writes a purchase and takes the money out of that account. It is not a correction. If
        the record was simply wrong, fix the lot below instead.
      </div>

      <div className="fab-row" style={{ marginTop: 0 }}>
        <button className="btn btn-block" onClick={reset}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!ready || saving}
                onClick={() => void commit()}>
          {saving ? 'Recording…' : 'Record the purchase'}
        </button>
      </div>
    </div>
  );
}
